'use client';

import React, { useState } from 'react';
import { useTranslations } from 'next-intl';
import { StoreFooter } from '../../../components/StoreChrome';
import SiteHeader from '../../../components/SiteHeader';
import { useCart } from '../../../components/CartProvider';
import { CartItem, money, moneyMensal } from '../../../lib/store-api';

function LinhaCarrinho({ item }: { item: CartItem }) {
  const t = useTranslations('paginaCarrinho');
  const { updateItem, removeItem } = useCart();
  const [busy, setBusy] = useState(false);

  async function mudar(qtd: number) {
    if (qtd < 1 || busy) return;
    setBusy(true);
    try {
      await updateItem(item.id, qtd);
    } finally {
      setBusy(false);
    }
  }

  async function remover() {
    if (busy) return;
    setBusy(true);
    try {
      await removeItem(item.id);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="flex items-center gap-4 py-5"
      style={{ borderBottom: '1px solid #f0f0f0', opacity: busy ? 0.5 : 1 }}
    >
      <div
        className="bg-gray-100 flex-shrink-0"
        style={{ width: '72px', height: '72px', borderRadius: '4px', overflow: 'hidden' }}
      >
        {item.imagem ? (
          <img src={item.imagem} alt={item.nome} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full" />
        )}
      </div>

      <div className="flex-1 min-w-0">
        <a
          href={`/produto/${item.slug}`}
          className="text-sm font-medium hover:underline block truncate"
        >
          {item.nome}
        </a>
        {item.indisponivel && (
          <p className="text-red-600 text-xs mt-1">
            {t('itemIndisponivel')}
          </p>
        )}
        <p className="text-gray-500 text-xs mt-1">{moneyMensal(item.precoUnit)} {t('precoUnitSufixo')}</p>
      </div>

      <div className="flex items-center border border-gray-300 rounded flex-shrink-0">
        <button
          onClick={() => mudar(item.quantidade - 1)}
          className="px-2 py-1 hover:bg-gray-100"
          aria-label={t('diminuirAria')}
        >
          −
        </button>
        <span className="px-3 text-sm" style={{ minWidth: '20px', textAlign: 'center' }}>
          {item.quantidade}
        </span>
        <button
          onClick={() => mudar(item.quantidade + 1)}
          className="px-2 py-1 hover:bg-gray-100"
          aria-label={t('aumentarAria')}
        >
          +
        </button>
      </div>

      <p
        className="font-semibold text-sm flex-shrink-0"
        style={{ width: '110px', textAlign: 'right' }}
      >
        {moneyMensal(item.total)}
      </p>

      <button
        onClick={remover}
        aria-label={t('removerAria')}
        className="p-2 hover:bg-gray-100 rounded flex-shrink-0"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>
  );
}

export default function CarrinhoPage() {
  const t = useTranslations('paginaCarrinho');
  const { cart, loading } = useCart();
  const temIndisponivel = cart.itens.some((i) => i.indisponivel);

  return (
    <>
      <SiteHeader />
      <main className="bg-white" style={{ minHeight: '70vh' }}>
        <div className="container-site" style={{ padding: '48px 0 96px', maxWidth: '900px' }}>
          <h1 className="display-section mb-10" style={{ fontSize: 'clamp(32px, 4vw, 56px)' }}>
            {t('titulo')}
          </h1>

          {loading && <p className="text-gray-500 text-sm">{t('carregando')}</p>}

          {!loading && cart.itens.length === 0 && (
            <div>
              <p className="text-gray-600 mb-6">{t('carrinhoVazio')}</p>
              <a href="/catalogo" className="btn-primary">
                {t('verCatalogo')}
              </a>
            </div>
          )}

          {!loading && cart.itens.length > 0 && (
            <>
              <div>
                {cart.itens.map((item) => (
                  <LinhaCarrinho key={item.id} item={item} />
                ))}
              </div>

              <div className="mt-8 flex justify-end">
                <div style={{ width: '280px' }}>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-600">{t('subtotalMensal')}</span>
                    <span>{moneyMensal(cart.subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-sm mb-4">
                    <span className="text-gray-600">{t('frete')}</span>
                    <span>{cart.frete > 0 ? money(cart.frete) : t('freteGratis')}</span>
                  </div>
                  <div
                    className="flex justify-between font-semibold pt-3"
                    style={{ fontSize: '18px', borderTop: '1px solid #000' }}
                  >
                    <span>{t('totalMensal')}</span>
                    <span>{moneyMensal(cart.total)}</span>
                  </div>

                  {temIndisponivel && (
                    <p className="text-red-600 text-xs mt-3">
                      {t('avisoIndisponivel')}
                    </p>
                  )}

                  <a
                    href="/checkout"
                    className="btn-primary block text-center mt-6"
                    style={{
                      pointerEvents: temIndisponivel ? 'none' : 'auto',
                      opacity: temIndisponivel ? 0.4 : 1,
                    }}
                  >
                    {t('fecharPedido')}
                  </a>
                  <p className="text-gray-500 text-xs mt-3 text-center">
                    {t('avisoSemPagamento')}
                  </p>
                </div>
              </div>
            </>
          )}
        </div>
      </main>
      <StoreFooter />
    </>
  );
}
